import React, { useCallback, useState } from 'react';
import { Worlds, errorMessage } from '../api/client';
import { ServerAction, World } from '../types';
import { Confirm } from '../components/ui/primitives';

const COPY: Record<ServerAction, { title: (w: World) => string; body: string; label: string; danger: boolean }> = {
  start: {
    title: (w) => `Start ${w.name}?`,
    body: 'A server is provisioned and the world boots. This usually takes a minute or two, and if you are on the hub you are moved in when it is ready. If nobody joins, the world shuts itself down again.',
    label: 'Start world',
    danger: false,
  },
  stop: {
    title: (w) => `Stop ${w.name}?`,
    body: 'A backup is taken first, then the server shuts down and its cloud instance is deleted. Connected players will be disconnected.',
    label: 'Stop world',
    danger: true,
  },
  kill: {
    title: (w) => `Kill ${w.name}?`,
    body: 'The cloud instance is deleted immediately without taking a backup. Anything since the last backup is lost. This cannot be undone.',
    label: 'Kill server',
    danger: true,
  },
};

/**
 * Shared confirm-and-run flow for start / stop / kill.
 * Returns a `request` trigger, the modal element to render, and a helper to know which world is busy.
 */
export function useServerAction(onDone?: (world: World, action: ServerAction) => void) {
  const [pending, setPending] = useState<{ world: World; action: ServerAction } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback((world: World, action: ServerAction) => {
    setError(null);
    setPending({ world, action });
  }, []);

  const cancel = useCallback(() => {
    if (busy) return;
    setPending(null);
    setError(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await Worlds.serverAction(pending.world.id, pending.action);
      setPending(null);
      onDone && onDone(pending.world, pending.action);
    } catch (e) {
      setError(errorMessage(e, `Failed to ${pending.action} the server. Please try again.`));
    } finally {
      setBusy(false);
    }
  }, [pending, onDone]);

  const copy = pending ? COPY[pending.action] : null;
  const modal = (
    <Confirm
      open={!!pending}
      title={pending && copy ? copy.title(pending.world) : ''}
      body={copy ? copy.body : ''}
      confirmLabel={copy ? copy.label : 'Confirm'}
      danger={copy ? copy.danger : false}
      busy={busy}
      error={error}
      onCancel={cancel}
      onConfirm={confirm}
    />
  );

  const busyFor = useCallback((worldId: string) => busy && pending?.world.id === worldId, [busy, pending]);

  return { request, modal, busyFor };
}
